import { useState, useCallback } from "react";
import { QueueOrderSearchStatus } from "../../../../../../../models/queue/queueOrderSearchRequest";

interface UseStatusSelectionReturn {
  selectedStatuses: QueueOrderSearchStatus[];
  appliedStatuses: QueueOrderSearchStatus[];
  statusDropdownOpen: boolean;
  setStatusDropdownOpen: (open: boolean) => void;
  handleStatusChange: (event: any) => void;
  handleStatusClose: () => void;
  applyStatusFilter: () => void;
  resetStatuses: () => void;
}

interface UseStatusSelectionProps {
  onStatusChange?: () => void;
  onPageReset?: () => void;
}

export const useStatusSelection = ({
  onStatusChange,
  onPageReset,
}: UseStatusSelectionProps = {}): UseStatusSelectionReturn => {
  const defaultStatuses = [
    QueueOrderSearchStatus.All,
    QueueOrderSearchStatus.Pending,
    QueueOrderSearchStatus.InProgress,
    QueueOrderSearchStatus.Done,
    QueueOrderSearchStatus.Scheduled
  ];

  const [selectedStatuses, setSelectedStatuses] = useState<QueueOrderSearchStatus[]>(defaultStatuses);
  const [appliedStatuses, setAppliedStatuses] = useState<QueueOrderSearchStatus[]>(defaultStatuses);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  // Handle status selection with All option logic
  // Helper function to get all non-"All" status options
  const getAllNonAllOptions = useCallback(() => [
    QueueOrderSearchStatus.Pending,
    QueueOrderSearchStatus.InProgress,
    QueueOrderSearchStatus.Done,
    QueueOrderSearchStatus.Scheduled
  ], []);

  // Helper function to handle when "All" option is clicked
  const handleAllOptionClick = useCallback((value: QueueOrderSearchStatus[]) => {
    if (value.includes(QueueOrderSearchStatus.All)) {
      // "All" was just selected - select all options
      setSelectedStatuses([
        QueueOrderSearchStatus.All,
        ...getAllNonAllOptions()
      ]);
    } else {
      // "All" was just deselected - unselect all options
      setSelectedStatuses([]);
    }
  }, [getAllNonAllOptions]);

  // Helper function to handle individual option changes when "All" is currently selected
  const handleIndividualChangeWithAllSelected = useCallback((value: QueueOrderSearchStatus[]) => {
    const allNonAllOptions = getAllNonAllOptions();
    const wasOptionRemoved = selectedStatuses.some(
      status => allNonAllOptions.includes(status) && !value.includes(status)
    );
    
    if (wasOptionRemoved) {
      // Remove "All" and keep only the remaining individual options
      setSelectedStatuses(value.filter(v => v !== QueueOrderSearchStatus.All));
    } else {
      // An individual option was added while "All" is selected
      setSelectedStatuses(value);
    }
  }, [selectedStatuses, getAllNonAllOptions]);

  // Helper function to handle individual option changes when "All" is not selected
  const handleIndividualChangeWithoutAll = useCallback((value: QueueOrderSearchStatus[]) => {
    if (value.length === 0) {
      setSelectedStatuses([]);
      return;
    }

    const allNonAllOptions = getAllNonAllOptions();
    const hasAllNonAllOptions = allNonAllOptions.every(option => value.includes(option));
    
    if (hasAllNonAllOptions) {
      // If all individual options are selected, add "All" as well
      setSelectedStatuses([QueueOrderSearchStatus.All, ...value]);
    } else {
      setSelectedStatuses(value);
    }
  }, [getAllNonAllOptions]);

  const handleStatusChange = useCallback((event: any) => {
    const value = event.target.value as QueueOrderSearchStatus[];
    
    // Check if "All" was just clicked
    const allWasClicked = value.includes(QueueOrderSearchStatus.All) !== selectedStatuses.includes(QueueOrderSearchStatus.All);
    
    if (allWasClicked) {
      handleAllOptionClick(value);
    } else if (selectedStatuses.includes(QueueOrderSearchStatus.All)) {
      handleIndividualChangeWithAllSelected(value);
    } else {
      handleIndividualChangeWithoutAll(value);
    }
  }, [selectedStatuses, handleAllOptionClick, handleIndividualChangeWithAllSelected, handleIndividualChangeWithoutAll]);

  // Handle dropdown close and trigger search
  const handleStatusClose = useCallback(() => {
    let finalStatuses = selectedStatuses;
    
    // If no options are selected when dismissing, select "All" and all options
    if (selectedStatuses.length === 0) {
      finalStatuses = [
        QueueOrderSearchStatus.All,
        QueueOrderSearchStatus.Pending,
        QueueOrderSearchStatus.InProgress,
        QueueOrderSearchStatus.Done,
        QueueOrderSearchStatus.Scheduled
      ];
      setSelectedStatuses(finalStatuses);
    }
    
    // Check if status selection actually changed from what's currently applied
    const statusesChanged = 
      finalStatuses.length !== appliedStatuses.length ||
      finalStatuses.some(status => !appliedStatuses.includes(status)) ||
      appliedStatuses.some(status => !finalStatuses.includes(status));
    
    if (statusesChanged) {
      // Update applied statuses to trigger search
      setAppliedStatuses([...finalStatuses]);
      
      // Reset to first page when status filter changes
      onPageReset?.();
      
      // Trigger search with current status selection
      onStatusChange?.();
    }
  }, [selectedStatuses, appliedStatuses, onStatusChange, onPageReset]);

  // Apply status filter and close dropdown
  const applyStatusFilter = useCallback(() => {
    // Close the dropdown first
    setStatusDropdownOpen(false);
    // Then apply the filter logic
    handleStatusClose();
  }, [handleStatusClose]);

  const resetStatuses = useCallback(() => {
    const defaultStatusesArray = [
      QueueOrderSearchStatus.All,
      QueueOrderSearchStatus.Pending,
      QueueOrderSearchStatus.InProgress,
      QueueOrderSearchStatus.Done,
      QueueOrderSearchStatus.Scheduled
    ];
    setSelectedStatuses(defaultStatusesArray);
    setAppliedStatuses(defaultStatusesArray);
  }, []);

  return {
    selectedStatuses,
    appliedStatuses,
    statusDropdownOpen,
    setStatusDropdownOpen,
    handleStatusChange,
    handleStatusClose,
    applyStatusFilter,
    resetStatuses,
  };
};
